import axios from 'axios';
import { API_BASE } from './config';

/**
 * Fetch all coins
 * @param status Optional status filter
 * @returns List of coins
 */
export const fetchCoins = async (status?: string) => {
  try {
    const params = status ? { status } : {};
    const response = await axios.get(`${API_BASE}/coin`, { params });

    // Log the response for debugging
    console.log('Coin API raw response:', response);
    console.log('Coin API response data:', response.data);

    // Handle different response formats
    const data = response.data;

    // Return the data array directly
    if (data && data.data && Array.isArray(data.data)) {
      // Standard format with data property - return just the array
      return data.data;
    } else if (Array.isArray(data)) {
      // If the response is already an array, return it directly
      return data;
    } else {
      // If we can't determine the format, return an empty array
      console.warn('Unknown coin data format:', data);
      return [];
    }
  } catch (error: any) {
    console.error('Error fetching coins:', error);
    // Return empty array on error
    return [];
  }
};

/**
 * Fetch a coin by ID
 * @param id Coin ID
 * @returns Coin details
 */
export const fetchCoinById = async (id: string) => {
  try {
    const response = await axios.get(`${API_BASE}/coin/${id}`);
    // Return the coin data directly
    return response.data.data || null;
  } catch (error) {
    console.error(`Error fetching coin with ID ${id}:`, error);
    return null;
  }
};

/**
 * Create a new coin
 * @param coinData Coin data
 * @returns Created coin
 */
export const createCoin = async (coinData: any) => {
  try {
    const response = await axios.post(`${API_BASE}/coin`, coinData);
    return response.data.data || null;
  } catch (error) {
    console.error('Error creating coin:', error);
    throw error; // Rethrow to allow handling in the component
  }
};

/**
 * Update a coin
 * @param id Coin ID
 * @param coinData Updated coin data
 * @returns Updated coin
 */
export const updateCoin = async (id: string, coinData: any) => {
  try {
    const response = await axios.put(`${API_BASE}/coin/${id}`, coinData);
    return response.data.data || null;
  } catch (error) {
    console.error(`Error updating coin with ID ${id}:`, error);
    throw error; // Rethrow to allow handling in the component
  }
};

/**
 * Delete a coin
 * @param id Coin ID
 * @returns Response data (for soft delete) or void (for hard delete)
 */
export const deleteCoin = async (id: string) => {
  try {
    const response = await axios.delete(`${API_BASE}/coin/${id}`);
    // If the response has data (soft delete case), return it
    if (response.status === 200 && response.data && response.data.data) {
      return response.data.data;
    }
    // Otherwise, it's a hard delete (204 No Content)
    return null;
  } catch (error: any) {
    // If the error response contains data (soft delete case), return it
    if (error.response && error.response.status === 200 &&
        error.response.data && error.response.data.data) {
      return error.response.data.data;
    }
    // Otherwise, rethrow the error
    throw error;
  }
};
